import dayjs, { Dayjs } from 'dayjs';

export type MaintenancePeriodMode = 'week' | 'month' | 'year';
export type MaintenanceDateSortDirection = 'asc' | 'desc';
export type MaintenanceStatusCountMap = Record<number, number>;
export type MaintenanceSelectOption = { label: string; value: string };

export const PERIODICITY_OPTIONS = ['lunar', 'trimestrial', 'semestrial', 'anual', 'la cerere'];

export const MAINTENANCE_SORT_OPTIONS: { label: string; value: MaintenanceDateSortDirection }[] = [
  { label: 'Data crescator', value: 'asc' },
  { label: 'Data descrescator', value: 'desc' },
];

export const STATUS_CONFIG: Record<number, { label: string; color: string }> = {
  1: { label: 'Netransmis', color: '#8c8c8c' },
  2: { label: 'Transmis', color: '#1677ff' },
  3: { label: 'A ajuns', color: '#52c41a' },
  4: { label: 'Finalizata', color: '#13c2c2' },
  5: { label: 'Cerere reprogramare', color: '#faad14' },
};

export const ACTIVE_STATUS_OPTIONS = [2, 3, 5].map((value) => ({
  value,
  label: STATUS_CONFIG[value].label,
}));

export const HISTORY_STATUS_OPTIONS = [4].map((value) => ({
  value,
  label: 'Finalizate',
}));

export const buildStatusOptionsWithCounts = (
  options: { value: number; label: string }[],
  counts: MaintenanceStatusCountMap = {},
) =>
  options.map((option) => ({
    ...option,
    label: `${option.label} (${counts[option.value] ?? 0})`,
  }));

export const VIEW_TO_FULLCALENDAR: Record<MaintenancePeriodMode, string> = {
  week: 'dayGridWeek',
  month: 'dayGridMonth',
  year: 'multiMonthYear',
};

export const getStatusDisplay = (statusId?: number, fallbackName?: string) => {
  if (statusId && STATUS_CONFIG[statusId]) return STATUS_CONFIG[statusId];
  return { label: fallbackName || 'Status necunoscut', color: '#722ed1' };
};

export const getClientName = (record: any) =>
  record?.metadata?.company?.name ?? record?.company?.name ?? 'Client necunoscut';

export const getClientId = (record: any) =>
  record?.metadata?.company?.id ?? record?.company?.id ?? record?.companyId;

export const getWorkPlaceName = (record: any) =>
  record?.metadata?.workPlace?.name ?? record?.workPlace?.name ?? 'Punct necunoscut';

export const getWorkPlaceId = (record: any) =>
  record?.metadata?.workPlace?.id ?? record?.workPlace?.id ?? record?.workPlaceId;

export const getContractNumber = (record: any) => record?.contract?.number ?? '';

export const getMaintenanceStatusId = (record: any) => record?.statusId ?? record?.status?.id;

export const getMaintenanceDisplayDate = (record: any) => {
  const statusId = getMaintenanceStatusId(record);
  return statusId === 4 ? record?.interventionFile?.deliveryDate : record?.appointment || record?.dateEnd;
};

export const getPeriodStart = (periodMode: MaintenancePeriodMode, value: Dayjs) => {
  if (periodMode === 'week') {
    const dayOfWeek = value.day();
    const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    return value.add(diffToMonday, 'day').startOf('day');
  }

  if (periodMode === 'year') return value.startOf('year');

  return value.startOf('month');
};

export const getPeriodEnd = (periodMode: MaintenancePeriodMode, value: Dayjs) => {
  if (periodMode === 'week') return getPeriodStart(periodMode, value).add(6, 'day').endOf('day');
  if (periodMode === 'year') return value.endOf('year');
  return value.endOf('month');
};

export const getPeriodRangeForApi = (periodMode: MaintenancePeriodMode, value: Dayjs) => ({
  startDate: getPeriodStart(periodMode, value).format('YYYY-MM-DD'),
  endDate: getPeriodEnd(periodMode, value).format('YYYY-MM-DD'),
});

export const getDatePickerFormat = (periodMode: MaintenancePeriodMode) => {
  if (periodMode === 'year') return 'YYYY';
  if (periodMode === 'month') return 'MM.YYYY';
  return 'DD.MM.YYYY';
};

export const getDatePickerValue = (periodMode: MaintenancePeriodMode, value: Dayjs) =>
  periodMode === 'week' ? getPeriodStart(periodMode, value) : value;

export const formatDisplayDate = (value?: string | null) =>
  value && dayjs(value).isValid() ? dayjs(value).format('DD-MM-YYYY') : '-';

export const buildUniqueFilterOptions = (
  records: any[],
  getValue: (record: any) => string | undefined | null,
  getLabel: (record: any) => string | undefined | null,
): MaintenanceSelectOption[] => {
  const optionsByValue = new Map<string, string>();

  records.forEach((record) => {
    const value = getValue(record);
    const label = getLabel(record);
    if (!value || !label) return;
    optionsByValue.set(value, label);
  });

  return Array.from(optionsByValue.entries())
    .map(([value, label]) => ({ value, label }))
    .sort((left, right) => left.label.localeCompare(right.label, 'ro', { sensitivity: 'base' }));
};

export const buildSystemTypeOptions = (records: any[]): MaintenanceSelectOption[] => {
  const optionsByValue = new Map<string, string>();

  records.forEach((record) => {
    const systemTypes = record?.mentenancePlanningSystemTypes ?? record?.maintenancePlanningSystemTypes ?? [];
    systemTypes.forEach((entry: any) => {
      const value = entry?.systemType?.id ?? entry?.systemTypeId;
      const label = entry?.systemType?.name;
      if (!value || !label) return;
      optionsByValue.set(value, label);
    });
  });

  return Array.from(optionsByValue.entries())
    .map(([value, label]) => ({ value, label }))
    .sort((left, right) => left.label.localeCompare(right.label, 'ro', { sensitivity: 'base' }));
};

const compareMaintenanceDate = (
  left: any,
  right: any,
  sortDirection: MaintenanceDateSortDirection = 'asc',
) => {
  const direction = sortDirection === 'asc' ? 1 : -1;
  const leftDate = getMaintenanceDisplayDate(left);
  const rightDate = getMaintenanceDisplayDate(right);
  const hasLeftDate = !!leftDate && dayjs(leftDate).isValid();
  const hasRightDate = !!rightDate && dayjs(rightDate).isValid();

  if (!hasLeftDate && !hasRightDate) return 0;
  if (!hasLeftDate) return 1;
  if (!hasRightDate) return -1;

  const leftValue = dayjs(leftDate).valueOf();
  const rightValue = dayjs(rightDate).valueOf();
  return (leftValue - rightValue) * direction;
};

export const sortByMaintenanceDate = (
  records: any[],
  sortDirection: MaintenanceDateSortDirection = 'asc',
) => records.slice().sort((left, right) => compareMaintenanceDate(left, right, sortDirection));
